'use client';

import { useStripe } from '@stripe/react-stripe-js';
import React, { useState } from 'react';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import Modal from '@cineamo/ui/src/modals/Modal';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import type { BuyerDetails } from './actions';
import type { Wallet } from './ExpressCheckout';

const MOCK_WALLET_KEY = 'mock-wallet';
const MOCK_BUYER: BuyerDetails = { email: 'test@example.com', firstName: 'Max', lastName: 'Mustermann' };

/**
 * Dev builds only: `?mockWallet=apple_pay` (or `google_pay`) makes the checkout behave as if the device had that
 * wallet, for the `.test` domains where Apple Pay never shows (Stripe registers no such domain). Kept for the tab
 * in sessionStorage, so it survives the redirects; `?mockWallet=` turns it off. A production build drops it.
 */
export function mockWallet(): Wallet | null {
    if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return null;
    try {
        const param = new URLSearchParams(window.location.search).get('mockWallet');
        if (param !== null) window.sessionStorage.setItem(MOCK_WALLET_KEY, param);
        const stored = window.sessionStorage.getItem(MOCK_WALLET_KEY);
        return stored === 'apple_pay' || stored === 'google_pay' ? stored : null;
    } catch {
        return null;
    }
}

type MockWalletButtonProps = {
    wallet: Wallet;
    amount: number;
    /** Runs the payment the way the real wallet's confirm event does, with the given buyer and confirm step. */
    onPay: (
        buyer: BuyerDetails,
        confirm: (clientSecret: string) => Promise<{ error?: { message?: string } }>
    ) => Promise<void>;
};

/** Stands in for the Express Checkout Element: a wallet-looking button and a sheet with what the wallet would send. */
export default function MockWalletButton({ wallet, amount, onPay }: MockWalletButtonProps): React.JSX.Element {
    const stripe = useStripe();
    const formatter = useFormat();
    const { currency } = useCartProvider();
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [isPaying, setIsPaying] = useState(false);
    const name = wallet === 'apple_pay' ? 'Apple Pay' : 'Google Pay';

    const pay = async () => {
        if (!stripe) return;
        setIsPaying(true);
        // Stripe's test card stands in for the wallet's card token.
        await onPay(MOCK_BUYER, (clientSecret) =>
            stripe.confirmCardPayment(clientSecret, { payment_method: 'pm_card_visa' })
        );
        setIsPaying(false);
        setIsSheetOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setIsSheetOpen(true)}
                className="flex h-48 w-full items-center justify-center rounded-4 bg-black text-body-bold-16 text-white outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]">
                {wallet === 'apple_pay' ? ' Pay' : 'G Pay'}
            </button>
            {isSheetOpen && (
                <Modal
                    modalIsOpen={isSheetOpen}
                    setModalIsOpen={setIsSheetOpen}
                    onClose={isPaying ? undefined : () => setIsSheetOpen(false)}
                    title={`${name} (Mock)`}
                    className="max-w-[420px]">
                    <div className="flex flex-col space-y-16">
                        <p className="text-headline-20">{formatter.number.formatCurrency(amount / 100, currency)}</p>
                        <dl className="grid grid-cols-[auto_1fr] gap-x-16 gap-y-8 text-body-14">
                            <dt className="opacity-50">Name</dt>
                            <dd>
                                {MOCK_BUYER.firstName} {MOCK_BUYER.lastName}
                            </dd>
                            <dt className="opacity-50">E-Mail</dt>
                            <dd>{MOCK_BUYER.email}</dd>
                            <dt className="opacity-50">Karte</dt>
                            <dd>Visa •••• 4242</dd>
                        </dl>
                        <button
                            type="button"
                            onClick={pay}
                            disabled={isPaying}
                            className="flex h-48 items-center justify-center gap-8 rounded-4 bg-black text-body-bold-16 text-white disabled:opacity-50">
                            {isPaying && <IconLoading16 className="animate-spin" />}
                            {wallet === 'apple_pay' ? 'Mit Face ID bezahlen' : 'Bezahlen'}
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
}
